import { Check, Flower2, HandHeart, PhoneCall, Truck } from "lucide-react";
import { Seo } from "../components/Seo";
import { Eyebrow, GhostLink, Reveal } from "../components/ui-bits";
import { CTABand, PageHero, SectionHeading } from "../components/PageSections";
import { SITE } from "../data/site";

const TRIBUTES = [
  {
    title: "Casket Sprays",
    text: "The family's central tribute, designed in close consultation — favorite flowers, favorite colors, a life remembered well.",
    image: "/images/01_Casket_Sprays.webp",
    imageAlt: "Elegant floral casket spray with roses, lilies, and hydrangea by JIF – Jack Iannotti Flowers",
  },
  {
    title: "Standing Sprays & Wreaths",
    text: "Formal tributes on easels for the service, delivered to the funeral home before the first visiting hours.",
    image: "/images/02_Standing_Sprays_and_Wreaths.webp",
    imageAlt: "Standing floral spray and sympathy wreath on easels by JIF – Jack Iannotti Flowers",
  },
  {
    title: "Vase & Basket Arrangements",
    text: "Warm, personal designs from friends, neighbors, and coworkers — appropriate for the service or the family's home.",
    image: "/images/03_Vase_and_Basket_Arrangements.webp",
    imageAlt: "Sympathy vase and basket arrangements in soft whites and pinks by JIF – Jack Iannotti Flowers",
  },
  {
    title: "Plants & Dish Gardens",
    text: "A living remembrance that stays with the family long after the service — our most requested sympathy gift for the home.",
    image: "/images/04_Plants_and_Dish_Gardens.webp",
    imageAlt: "Green plants and dish gardens for sympathy by JIF – Jack Iannotti Flowers",
  },
];

const STEPS = [
  {
    n: "01",
    icon: PhoneCall,
    title: "Call the shop",
    text: "Tell us the name, the funeral home, and the service times. A few gentle questions — your relationship, favorite flowers or colors — and we have everything we need.",
  },
  {
    n: "02",
    icon: Flower2,
    title: "We design & coordinate",
    text: "Your tribute is designed fresh in our Coventry shop and moves to the front of the bench. We call the funeral home directly to confirm timing — you never manage logistics.",
  },
  {
    n: "03",
    icon: Truck,
    title: "Hand-delivered & confirmed",
    text: "Our own driver delivers before visiting hours, places the tribute with the funeral home staff, and confirms every delivery. On time, exactly as ordered.",
  },
];

export default function Sympathy() {
  return (
    <>
      <Seo
        title="Funeral Flowers Coventry RI | JIF – Jack Iannotti Flowers"
        description="Sympathy and funeral flowers in Coventry, RI since 1974. Casket sprays, standing sprays & home tributes, coordinated directly with funeral homes. Call (401) 822-4440."
      />
      <PageHero
        eyebrow="Sympathy & Funeral Flowers"
        lines={["When Words Are Hard,", "Flowers Help"]}
        lede="Since 1974, Coventry families have trusted us with their hardest goodbyes. One phone call, and we take gentle care of everything."
        image="https://images.unsplash.com/photo-1724152312974-d4d48b8b36fd?crop=entropy&cs=srgb&fm=jpg&q=85"
        imageAlt="Elegant white lily sympathy tribute arrangement by JIF Flowers"
        secondary={{ href: "/journal/funeral-flower-etiquette-guide", label: "Funeral Flower Etiquette", testid: "hero-etiquette-link" }}
      />

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-24">
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <Eyebrow>How We Help</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl font-medium leading-snug text-plum sm:text-4xl lg:text-[42px] lg:leading-[1.15]">
              One phone call. We handle the rest.
            </h2>
            <p className="mt-6 text-[17px] leading-relaxed text-charcoal/85">
              When you call, we'll ask a few gentle questions — the service details, your relationship, what you'd
              like to say — and take care of everything else. We work directly with area funeral homes every week, so
              your tribute arrives on time, exactly as ordered, without you managing any logistics.
            </p>
            <p className="mt-4 text-[17px] leading-relaxed text-charcoal/85">
              If the timing is urgent, tell us. Sympathy orders move to the front of our design bench, and every
              delivery is hand-carried by our own driver and confirmed.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Direct coordination with area funeral homes",
                "Sympathy orders are always our first priority",
                "Every delivery hand-carried and confirmed",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-[16px] text-charcoal/90">
                  <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-plum/10">
                    <Check className="h-3.5 w-3.5 text-plum" strokeWidth={3} aria-hidden="true" />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -left-4 -top-4 h-full w-full rounded-xl border border-gold/60" aria-hidden="true" />
              <img
                src="https://images.unsplash.com/photo-1674784708710-0f03d1868ed3?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Peaceful white lily tribute arrangement designed at JIF Flowers"
                loading="lazy"
                className="relative aspect-[4/5] w-full rounded-xl object-cover shadow-xl shadow-plum/15"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-sagetint">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-24">
          <SectionHeading
            eyebrow="Tributes"
            title="Ways to honor a life"
            lede="If you're unsure what to send, call the shop — after fifty years, we can guide you in two quiet minutes."
          />
          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            {TRIBUTES.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.08}>
                <div className="h-full overflow-hidden rounded-xl bg-ivory shadow-sm" data-testid={`tribute-card-${i}`}>
                  <img
                    src={t.image}
                    alt={t.imageAlt}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover"
                  />
                  <div className="p-8">
                    <h3 className="font-serif text-2xl font-medium text-plum">{t.title}</h3>
                    <div className="gold-rule-left mt-3" aria-hidden="true" />
                    <p className="mt-3 text-[15px] leading-relaxed text-charcoal/80">{t.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-24">
        <SectionHeading
          eyebrow="How It Works"
          title="Three steps, all handled by us"
          align="center"
        />
        <div className="mt-14 grid gap-10 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.1} className="text-center">
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-gold/50 bg-white/70">
                <s.icon className="h-7 w-7 text-sage" strokeWidth={1.5} aria-hidden="true" />
              </span>
              <p className="mt-5 font-serif text-lg italic text-gold">{s.n}</p>
              <h3 className="mt-1 font-serif text-2xl font-medium text-plum">{s.title}</h3>
              <p className="mx-auto mt-3 max-w-xs text-[15px] leading-relaxed text-charcoal/80">{s.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-gold/25 bg-white/50">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-20 sm:px-8 lg:grid-cols-2 lg:gap-20">
          <Reveal className="order-last lg:order-first">
            <img
              src="https://images.unsplash.com/photo-1558879860-45f24b366ea1?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Blooming plant and dish garden for the home from JIF Flowers"
              loading="lazy"
              className="aspect-[4/3] w-full rounded-xl object-cover shadow-lg shadow-plum/10"
            />
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex items-center gap-3">
              <HandHeart className="h-6 w-6 text-sage" strokeWidth={1.6} aria-hidden="true" />
              <Eyebrow>For the Home</Eyebrow>
            </div>
            <h2 className="mt-4 font-serif text-3xl font-medium leading-snug text-plum sm:text-4xl">
              A living remembrance
            </h2>
            <p className="mt-5 text-[17px] leading-relaxed text-charcoal/85">
              Many families tell us the flowers they remember most are the ones that arrived in the quiet weeks after
              the service. A dish garden or blooming plant sent to the home is a gentle way to say you're still
              thinking of them — and it lasts for months, not days.
            </p>
            <div className="mt-7">
              <GhostLink href="/plants-gifts" testid="sympathy-plants-link">
                Plants & Dish Gardens
              </GhostLink>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-5 py-20 text-center sm:px-8">
        <Reveal>
          <h2 className="font-serif text-3xl font-medium text-plum sm:text-4xl">Not sure what to say or send?</h2>
          <p className="mx-auto mt-5 max-w-xl text-[17px] leading-relaxed text-charcoal/80">
            Our short guide to funeral flower etiquette covers what to send, when, and what to write on the card —
            plain answers from fifty years of helping local families.
          </p>
          <div className="mt-7">
            <GhostLink href="/journal/funeral-flower-etiquette-guide" testid="sympathy-etiquette-link">
              Read the Etiquette Guide
            </GhostLink>
          </div>
        </Reveal>
      </section>

      <CTABand
        title="We're here when you need us"
        note={`Call ${SITE.phone} and we'll take gentle care of everything. Sympathy orders are always our first priority.`}
      />
    </>
  );
}
